import { createAction } from "@reduxjs/toolkit";
import { AsyncThunk, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "app/rootReducer";
import { AppDispatch } from "app/store";
import { AuthType, CurrentUser, FirebaseRefs, ThirdPartyAuthProviders, UserRoles } from "interfaces";
import { UseFormSetError } from "react-hook-form";
import { toast } from "react-toastify";
import { AuthService, FirestoreService } from "services";

import { userActions } from "./userSlice";

export const setUser = createAction<CurrentUser>("setUser");
export const setUserCreatedInSession = createAction<boolean>("setUserCreatedInSession");

export const thirdPartyAuth: AsyncThunk<
  void,
  { role?: UserRoles; authType: AuthType; providerType: ThirdPartyAuthProviders; href: string },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  void,
  { role?: UserRoles; authType: AuthType; providerType: ThirdPartyAuthProviders; href: string },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("thirdPartyAuth", async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    const { href, ...rest } = payload;

    const response = await AuthService.thirdPartyAuth(rest);

    if (response.user) {
      if (href === "/get-started") {
        const { initialDetails } = getState().getStarted;

        if (payload.authType === AuthType.REGISTER) {
          await FirestoreService.setDocument({
            coll: FirebaseRefs.INITIAL_DETAILS,
            docId: response.user.uid,
            data: {
              data: initialDetails.data,
              step: initialDetails.step,
              uid: response.user.uid
            }
          });
        }

        //this will avoid refetching data when user is logged in as anonymous user
        dispatch(userActions.setUserCreatedInSession(true));
      }

      dispatch(userActions.setUser(response.user));
    }

    if (response.accessToken) {
      localStorage.setItem("token", response.accessToken);
    }

    return;
  } catch (err) {
    toast(err as string, { type: "error" });
    return rejectWithValue(err as string);
  }
});

export const register: AsyncThunk<
  void,
  {
    email: string;
    password: string;
  } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
    href: string;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  void,
  {
    email: string;
    password: string;
  } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
    href: string;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("register", async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    const { href, ...rest } = payload;

    const response = await AuthService.register(rest);
    toast.dismiss();

    if (response.user) {
      if (href === "/get-started") {
        const { initialDetails } = getState().getStarted;
        await FirestoreService.setDocument({
          coll: FirebaseRefs.INITIAL_DETAILS,
          data: {
            data: initialDetails.data,
            step: initialDetails.step,
            uid: response.user.uid
          },
          docId: response.user.uid
        });

        //this will avoid refetching data when user is logged in as anonymous user
        dispatch(userActions.setUserCreatedInSession(true));
      }

      dispatch(userActions.setUser(response.user));
    }

    if (response.accessToken) {
      localStorage.setItem("token", response.accessToken);
    }

    return;
  } catch (err) {
    return rejectWithValue(err as string);
  }
});

export const login: AsyncThunk<
  void,
  { email: string; password: string } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
    href: string;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  void,
  { email: string; password: string } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
    href: string;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("login", async (payload, { rejectWithValue, dispatch }) => {
  try {
    // anonymous user id i.e. UID
    // const uid = getState().user.current?.uid;
    // reference of anonymous user
    // const anonymousUser = auth.currentUser;

    const { href, ...rest } = payload;
    const response = await AuthService.login(rest);
    toast.dismiss();

    // if (href === "/get-started" && uid) {
    //   // delete the anonymous user initial details document
    //   await FirestoreService.deleteDocument({ coll: FirebaseRefs.INITIAL_DETAILS, docId: uid });
    //   // delete the anonymous user
    //   typeof anonymousUser?.delete === "function" && (await AuthService.deleteUser(anonymousUser));
    // }

    if (response?.user) {
      // if (href === "/get-started") {
      //   const { initialDetails } = getState().getStarted;
      //   await FirestoreService.updateDocument({
      //     coll: FirebaseRefs.INITIAL_DETAILS,
      //     docId: response.user.uid,
      //     data: {
      //       data: initialDetails.data,
      //       step: initialDetails.step,
      //       uid: response.user.uid,
      //     }
      //   });
      // }

      dispatch(userActions.setUser(response.user));
    }

    if (response?.accessToken) {
      localStorage.setItem("token", response.accessToken);
    }

    return;
  } catch (err) {
    return rejectWithValue(err as string);
  }
});

export const checkAuthState: AsyncThunk<
  CurrentUser | null,
  void,
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  CurrentUser | null,
  void,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("checkAuthState", async (_, { rejectWithValue, dispatch, getState }) => {
  try {
    const payload = {
      uid: getState().user.current!.uid,
      role: getState().user.current!.role
    };
    const response = await AuthService.checkAuthState(payload);
    return response;
  } catch (err) {
    dispatch({ type: "resetUser" });
    return rejectWithValue(err as string);
  }
});

export const logout: AsyncThunk<
  null,
  void,
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  null,
  void,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("logout", async (_, { rejectWithValue, dispatch }) => {
  try {
    await AuthService.logOut();
    dispatch({
      type: "reset/store"
    });
    return null;
  } catch (err) {
    dispatch({ type: "resetUser" });
    return rejectWithValue(err as string);
  }
});

export const anonymousLogin: AsyncThunk<
  void,
  {
    cb: (val: CurrentUser) => void;
  },
  {
    state: RootState;
    rejectValue: string;
  }
> = createAsyncThunk<
  void,
  {
    cb: (val: CurrentUser) => void;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("anonymousLogin", async (payload, { rejectWithValue, dispatch }) => {
  try {
    const response = await AuthService.anonymousLogin();

    const token = (response as any)?.accessToken;

    if (token) {
      localStorage.setItem("token", token);
    }

    if (response) {
      const userData = {
        email: "",
        uid: response.uid,
        role: UserRoles.user
      };
      payload.cb(userData);

      //set user data
      dispatch(userActions.setUser(userData));
    }

    return;
  } catch (err) {
    return rejectWithValue(err as string);
  }
});
